/**
 * accessTokenExpiry — read a JWT's `exp` claim on the client, WITHOUT
 * verifying it, to decide one thing: whether painting the app shell with
 * this token would only lead to a 401 → refresh → (maybe) login bounce.
 *
 * Tester report 2026-10-21: "the page loaded and I was already logged in,
 * but then it switched to the login page." App.js paints the authenticated
 * shell from localStorage and lets the first request discover that the
 * access token has expired; when the refresh also fails, the shell is
 * replaced by the login page a second later. Reading `exp` up front lets
 * App.js run the refresh BEHIND a "Signing you back in…" splash, or go to
 * login directly when there is no refresh token to try.
 *
 * SECURITY POSTURE: this is a UX hint, never an authority. The payload is
 * decoded, not verified — a forged `exp` buys nothing, because every request
 * is still authenticated by the server. An unreadable token reads as NOT
 * expired, so a decode bug can never lock a student out: the app simply
 * behaves exactly as it did before this file existed.
 */

// Treat a token that expires within this many seconds as already expired:
// the first request would land after the line anyway, and a small skew
// covers clock drift between the student's device and the server.
export const EXPIRY_SKEW_SECONDS = 30;

/**
 * The `exp` claim (seconds since the epoch) of a JWT, or null when the token
 * is not a readable JWT or carries no numeric `exp`.
 *
 * @param {unknown} token
 * @returns {number|null}
 */
export function readJwtExp(token) {
  if (typeof token !== 'string' || token.length === 0) return null;
  const parts = token.split('.');
  if (parts.length < 2 || parts[1].length === 0) return null;
  try {
    // base64url → base64 (JWT payloads use the URL-safe alphabet, unpadded).
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    const payload = JSON.parse(atob(padded));
    const exp = Number(payload?.exp);
    return Number.isFinite(exp) ? exp : null;
  } catch {
    return null;
  }
}

/**
 * True when the token's `exp` is at or before now + skew. An unreadable token
 * (or one with no `exp`) returns false — see the posture note above.
 *
 * @param {unknown} token
 * @param {number} [nowMs=Date.now()] milliseconds since the epoch
 * @param {number} [skewSeconds=EXPIRY_SKEW_SECONDS]
 * @returns {boolean}
 */
export function isAccessTokenExpired(
  token,
  nowMs = Date.now(),
  skewSeconds = EXPIRY_SKEW_SECONDS,
) {
  const exp = readJwtExp(token);
  if (exp === null) return false;
  return exp * 1000 <= nowMs + skewSeconds * 1000;
}

export default isAccessTokenExpired;
